import NGContainer from '@/components/layout/NGContainer';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { cachedJsonFetch } from '../../../../service/cache';
import { fetch_request } from '../../../../service/request';
import CitySeoContent from './CitySeoContent';
import './HomepageFindGrave.css';
import HomepageFindGraveFirst from './HomepageFindGraveFirst';
import HomepageFindGraveThird from './HomepageFindGraveThird';
import { getFindGravePath } from '@/service/constants';

const HomepageFindGrave = () => {
  let { municipality, cemetery } = useParams();
  const intl = useIntl();
  const [municipalityName, setMunicipalityName] = useState('');
  const [cemeteryName, setCemeteryName] = useState('');

  useEffect(() => {
    if (!municipality) return;
    let cancelled = false;

    const loadMunicipality = async () => {
      try {
        const { data } = await cachedJsonFetch(`/api/municipalities/${municipality}`, {
          fetcher: fetch_request,
          cacheKey: `municipality:slug:${municipality}`,
          ttlMs: 60 * 60 * 1000,
        });
        if (cancelled) return;
        setMunicipalityName(data?.name || '');
      } catch (err) {
        console.log(err);
      }
    };

    loadMunicipality();

    return () => {
      cancelled = true;
    };
  }, [municipality]);

  useEffect(() => {
    if (!cemetery) return;
    let cancelled = false;

    const loadCemetery = async () => {
      try {
        const { data } = await cachedJsonFetch(`/api/graveyards/${cemetery}`, {
          fetcher: fetch_request,
          cacheKey: `graveyard:slug:${cemetery}`,
          ttlMs: 60 * 60 * 1000,
        });
        if (cancelled) return;
        setCemeteryName(data?.name || '');
      } catch (err) {
        console.log(err);
      }
    };

    loadCemetery();

    return () => {
      cancelled = true;
    };
  }, [cemetery]);

  const canonicalUrl = useMemo(() => {
    const canonicalPath = `${getFindGravePath()}${municipality ? `/${municipality}` : ''}${
      cemetery ? `/${cemetery}` : ''
    }`;
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://nettgrav.no';
    try {
      return new URL(canonicalPath, origin).toString();
    } catch {
      const normalizedOrigin = origin.endsWith('/') ? origin.slice(0, -1) : origin;
      return `${normalizedOrigin}${canonicalPath}`;
    }
  }, [municipality, cemetery]);

  const faqJsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: intl.formatMessage({id: 'find_grave_faq_question_1'}),
          acceptedAnswer: {
            '@type': 'Answer',
            text: intl.formatMessage({id: 'find_grave_faq_answer_1'}),
          },
        },
        {
          '@type': 'Question',
          name: intl.formatMessage({id: 'find_grave_faq_question_2'}),
          acceptedAnswer: {
            '@type': 'Answer',
            text: intl.formatMessage({id: 'find_grave_faq_answer_2'}),
          },
        },
        {
          '@type': 'Question',
          name: intl.formatMessage({id: 'find_grave_faq_question_3'}),
          acceptedAnswer: {
            '@type': 'Answer',
            text: intl.formatMessage({id: 'find_grave_faq_answer_3'}),
          },
        },
      ],
    }),
    [intl],
  );

  return (
    <>
      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <NGContainer>
        <HomepageFindGraveFirst municipalityName={municipalityName} cemeteryName={cemeteryName} />
        <HomepageFindGraveThird municipalityName={municipalityName} />
        <CitySeoContent />
      </NGContainer>
    </>
  );
};

export default HomepageFindGrave;
